import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { assignCredits, User } from '../../api/admin';

interface CreditsDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh?: boolean) => void;
}

const CreditsDialog: React.FC<CreditsDialogProps> = ({ open, user, onClose }) => {
  const { t } = useLanguage();
  const [credits, setCredits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCredits('');
    setError(null);
  }, [user]);

  const handleSubmit = async () => {
    if (!user || !credits) {
      setError(t('admin.users.validation.required'));
      return;
    }

    const amount = parseInt(credits);
    if (isNaN(amount) || amount < 0) {
      setError(t('admin.users.validation.invalidNumber'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await assignCredits(user.username, amount);
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign credits');
    } finally {
      setLoading(false);
    }
  };

  const currentCredits = user?.credits === 'unlimited' ? '∞' : user?.credits;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.actions.credits')} - {user?.username}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.users.currentCredits')}
          </Typography>
          <Typography variant="h4">
            {currentCredits}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('admin.users.columns.used')}: {user?.credits_used ?? 0}
          </Typography>
        </Box>

        <TextField
          label={t('admin.users.creditsToAdd')}
          type="number"
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
          fullWidth
          margin="normal"
          helperText={t('admin.users.creditsHelp')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('common.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditsDialog;
